import { useEffect, useMemo, useState } from 'react';
import { LoadingScreen } from './components/LoadingScreen';
import { heroById, matchHero } from './lib/algorithm';
import { ACTIVATION_TITLE, HOME_TITLE, preloadImage, PUBLIC_BACKGROUND, QUIZ_BACKGROUND } from './lib/imagePreloader';
import { navigate, routes, usePathname } from './lib/router';
import { loadState, restartQuiz, saveState, updateAnswer, withResult } from './lib/storage';
import { ActivationPage } from './pages/ActivationPage';
import { HomePage } from './pages/HomePage';
import { QuizPage } from './pages/QuizPage';
import { ResultPage } from './pages/ResultPage';
import { getResultCoverConfig } from './data/result-cover-config';
import { loadResultPreview } from './lib/resultPreview';
import type { Answer, Hero, PersistedState } from './types';

function preloadHeroCover(hero: Hero, priority: 'high' | 'auto' = 'high') {
  const titleAsset = getResultCoverConfig(hero).titleAsset;
  return Promise.all([
    preloadImage(hero.result_bg, priority),
    ...(titleAsset ? [preloadImage(titleAsset, priority)] : []),
  ]);
}

export default function App() {
  const pathname = usePathname();
  const [resultPreview] = useState(() => loadResultPreview(window.location.search));
  const [state, setState] = useState<PersistedState>(() => resultPreview ?? loadState());
  const [readyPublicView, setReadyPublicView] = useState<'activation' | 'home' | null>(null);
  const [quizReady, setQuizReady] = useState(false);
  const [readyHeroId, setReadyHeroId] = useState<number | null>(null);
  const [transitionMessage, setTransitionMessage] = useState<string | null>(null);
  const hero = useMemo(() => state.result ? heroById.get(state.result.heroId) : undefined, [state.result]);
  const publicView = pathname === '/home' || (pathname === '/' && state.activated) ? 'home' : 'activation';

  useEffect(() => {
    if (!resultPreview) saveState(state);
  }, [state, resultPreview]);

  useEffect(() => {
    if (pathname !== '/' && pathname !== '/activate' && pathname !== '/home') return;
    let active = true;
    const title = publicView === 'home' ? HOME_TITLE : ACTIVATION_TITLE;
    void Promise.all([
      preloadImage(PUBLIC_BACKGROUND, 'high'),
      preloadImage(title, 'high'),
    ]).then(() => {
      if (active) setReadyPublicView(publicView);
    });
    return () => { active = false; };
  }, [pathname, publicView]);

  useEffect(() => {
    if (readyPublicView === null && pathname !== '/quiz') return;
    let active = true;
    void preloadImage(QUIZ_BACKGROUND, pathname === '/quiz' ? 'high' : 'auto').then(() => {
      if (active) setQuizReady(true);
    });
    return () => { active = false; };
  }, [pathname, readyPublicView]);

  useEffect(() => {
    if (!hero) {
      setReadyHeroId(null);
      return;
    }
    let active = true;
    void preloadHeroCover(hero, 'high').then(() => {
      if (active) setReadyHeroId(hero.id);
    });
    return () => { active = false; };
  }, [hero]);

  useEffect(() => {
    const answers = state.answers.filter((answer): answer is Answer => Boolean(answer));
    if (answers.length !== 28 || state.result) return;
    const likelyResult = matchHero(answers);
    const likelyHero = heroById.get(likelyResult.heroId);
    if (likelyHero) void preloadHeroCover(likelyHero, 'high');
  }, [state.answers, state.result]);

  useEffect(() => {
    const valid = routes.includes(pathname as never);
    if (!valid || pathname === '/') {
      navigate(state.activated ? '/home' : '/activate', true);
      return;
    }
    if (!state.activated && pathname !== '/activate') {
      navigate('/activate', true);
      return;
    }
    if ((pathname === '/result' || pathname === '/report') && (!state.result || !hero)) {
      navigate('/home', true);
    }
  }, [pathname, state.activated, state.result, hero]);

  function activate() {
    setState((current) => ({ ...current, activated: true }));
    navigate('/home', true);
  }

  async function start() {
    setState((current) => current.result ? restartQuiz(current) : current);
    setTransitionMessage('正在进入心动之境');
    await preloadImage(QUIZ_BACKGROUND, 'high');
    setQuizReady(true);
    navigate('/quiz');
    setTransitionMessage(null);
  }

  function selectAnswer(answer: Answer) {
    setState((current) => updateAnswer(current, current.currentIndex, answer));
  }

  function move(index: number) {
    setState((current) => ({ ...current, currentIndex: Math.min(27, Math.max(0, index)) }));
  }

  async function complete() {
    const answers = state.answers.filter((answer): answer is Answer => Boolean(answer));
    if (answers.length !== 28) return;
    const result = matchHero(answers);
    const matchedHero = heroById.get(result.heroId);
    setTransitionMessage('正在揭晓你的盖世英雄');
    if (matchedHero) await preloadHeroCover(matchedHero, 'high');
    setState((current) => withResult(current, result));
    setReadyHeroId(result.heroId);
    navigate('/result');
    setTransitionMessage(null);
  }

  function restart() {
    setState((current) => restartQuiz(current));
    navigate('/home');
  }

  if (!routes.includes(pathname as never) && pathname !== '/') return null;
  if (transitionMessage) return <LoadingScreen message={transitionMessage} />;
  if ((pathname === '/' || pathname === '/activate' || pathname === '/home') && readyPublicView !== publicView) return <LoadingScreen message="画卷正在展开" />;
  if (pathname === '/quiz' && !quizReady) return <LoadingScreen message="正在进入心动之境" />;
  if ((pathname === '/result' || pathname === '/report') && hero && readyHeroId !== hero.id) return <LoadingScreen message="正在展开人物画卷" />;
  if (pathname === '/activate') return <ActivationPage onActivated={activate} />;
  if (pathname === '/home') return <HomePage onStart={start} />;
  if (pathname === '/quiz') return <QuizPage index={state.currentIndex} answers={state.answers} onSelect={selectAnswer} onMove={move} onComplete={complete} />;
  if ((pathname === '/result' || pathname === '/report') && hero && state.result) {
    return <ResultPage hero={hero} result={state.result} onRestart={restart} initialReport={pathname === '/report'} />;
  }
  return null;
}
